import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "react-query";
import { findAllLanguages } from "@/api/query/language";
import { getToSelectData } from "@/common/functions/getToSelectData";
import { WORD_LEVEL } from "@/common/constants/level.constants";
import { WORD_TYPE } from "@/common/constants/wordType.constants";
import {
  findWordsForTable,
  markWordStatusAsUserDecision,
} from "@/api/query/word";
import { Link, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faEdit,
  faQuestion,
  faX,
} from "@fortawesome/free-solid-svg-icons";
import { Color } from "@/common/colors/colors";
import { VerificationType } from "@/common/constants/verification.status.constants";
import usePagination from "@/common/hooks/use-pagination";
import { IconsWrapper } from "../AdminWordListPage.styled";
import { getCurrentStatusIcons } from "../helpers/get-current-status-icons";

const useAdminWordListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [addWord, setAddWord] = useState();
  const [options, setOptions] = useState([]);
  const [selectedLang, setSelectedLang] = useState();
  const [selectedType, setSelectedType] = useState();
  const [selectedLevel, setSelectedLevel] = useState();
  const [data, setData] = useState([]);
  const checkIfIncludeSentence = useRef();

  const {
    itemsTotal,
    limit,
    setLimit,
    skip,
    setSkip,
    isLoading,
    filtersChangeHandler,
    sortingChangeHandler,
  } = usePagination(setData, findWordsForTable, {
    skip: parseInt(searchParams.get("skip") || 0),
    limit: parseInt(searchParams.get("limit") || 10),
  });

  const tableData = useMemo(() => {
    return data.map(({ data: r, index }, i) => {
      return {
        no: index + 1 + ".",
        content:
          r.content +
          (r.core_word_from ? ` (${r.core_word_from.content})` : ""),
        level: r.level,
        type: r.type,
        comment: r.comment || "----",
        sentences:
          r.sentences
            ?.map(
              (s) =>
                s.content +
                (s.core_sentence_from
                  ? ` (${s.core_sentence_from.content})`
                  : "")
            )
            .join(" ,") || "-------",
        actions: r._id,
        errors: r.errors?.join(" ,") || "---",
        status: r?.veryfications
          ?.sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          )
          ?.shift()?.verificationStatus,
      };
    });
  }, [data]);

  const markOrderWordWithDecisionMutation = useMutation({
    mutationFn: (value) => markWordStatusAsUserDecision(value),
    onSuccess: () => {
      handleSearch();
    },
  });

  const handleMarkWordWithDecision = (decision, wordId) => {
    const payload = {
      decision,
      wordId,
    };

    markOrderWordWithDecisionMutation.mutate(payload);
  };

  const HEADERS = [
    {
      header: "No.",
      accessorKey: "no",
      size: 50,
      enableColumnFilter: false,
    },
    {
      header: "Status",
      accessorKey: "status",
      size: 50,
      cell: (info) => (
        <IconsWrapper>{getCurrentStatusIcons(info.getValue())}</IconsWrapper>
      ),
      enableColumnFilter: false,
    },
    {
      header: "Content",
      accessorKey: "content",
      size: 200,
    },
    {
      header: "Level",
      accessorKey: "level",
      size: 50,
      enableColumnFilter: false,
    },
    {
      header: "Type",
      accessorKey: "type",
      size: 150,
      enableColumnFilter: false,
    },
    {
      header: "Errors",
      accessorKey: "errors",
      size: 150,
      enableColumnFilter: false,
    },
    {
      header: "Comment",
      accessorKey: "comment",
      size: 150,
      enableColumnFilter: false,
    },
    {
      header: "Sentences",
      accessorKey: "sentences",
      size: 300,
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Actions",
      accessorKey: "actions",
      enableColumnFilter: false,
      enableSorting: false,
      size: 100,
      cell: (info) => (
        <IconsWrapper>
          <Link to={`/admin/word/${info.getValue()}`}>
            <FontAwesomeIcon
              icon={faEdit}
              style={{ color: Color.DarkBlue, cursor: "pointer" }}
            />{" "}
          </Link>
          <FontAwesomeIcon
            icon={faCheck}
            style={{ color: "green", cursor: "pointer" }}
            onClick={() =>
              handleMarkWordWithDecision(
                VerificationType.CORRECT,
                info.getValue()
              )
            }
          />{" "}
          <FontAwesomeIcon
            icon={faQuestion}
            style={{ color: "orange", cursor: "pointer" }}
            onClick={() =>
              handleMarkWordWithDecision(
                VerificationType.QUESTION,
                info.getValue()
              )
            }
          />{" "}
          <FontAwesomeIcon
            icon={faX}
            style={{ color: "red", cursor: "pointer" }}
            onClick={() =>
              handleMarkWordWithDecision(
                VerificationType.WRONG,
                info.getValue()
              )
            }
          />{" "}
        </IconsWrapper>
      ),
    },
  ];

  const findLanguagesMutation = useMutation({
    mutationFn: () => findAllLanguages(),
    onSuccess: ({ data }) => {
      const languageOptions = data.map((d) => ({
        label: d.name,
        value: d._id,
      }));

      setOptions(languageOptions);
      restoreFromUrl(languageOptions);
    },
  });

  const handleSearch = () => {
    const payload = {};

    payload.language = selectedLang.value;
    payload.levels = selectedLevel.map((sl) => sl.value);
    payload.types = selectedType.map((sl) => sl.value);
    payload.checkIfIncludeSentence = !!checkIfIncludeSentence.current.checked;

    const filters = Object.entries(payload).map(([key, value]) => ({
      id: key,
      value,
    }));

    filtersChangeHandler(filters);
    setSkip(0);
  };

  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    const params = new URLSearchParams(window.location.search);
    params.set("limit", limit);
    params.set("skip", skip);
    setSearchParams(params);
  }, [limit, skip]);

  const restoreFromUrl = (langOptions) => {
    let didRestore = false;
    const payload = {};

    const langParam = searchParams.get("lang");
    if (langParam) {
      const found = langOptions.find((o) => o.value === langParam);
      if (found) {
        setSelectedLang(found);
        didRestore = true;
        payload.language = found.value;
      }
    }

    if (!langParam) {
      const english = langOptions.find((d) => d.label === "English") || null;
      setSelectedLang(english);
      didRestore = true;
      payload.language = english.value;
    }

    const lvlParams = searchParams.getAll("level");
    if (lvlParams.length) {
      setSelectedLevel(
        getToSelectData(WORD_LEVEL).filter((o) => lvlParams.includes(o.value))
      );
      payload.levels = getToSelectData(WORD_LEVEL)
        .filter((o) => lvlParams.includes(o.value))
        .map((sl) => sl.value);
      didRestore = true;
    }

    if (!lvlParams.length) {
      setSelectedLevel(getToSelectData(WORD_LEVEL));
      payload.levels = getToSelectData(WORD_LEVEL).map((sl) => sl.value);
      didRestore = true;
    }

    const typeParams = searchParams.getAll("type");
    if (typeParams.length) {
      setSelectedType(
        getToSelectData(WORD_TYPE).filter((o) => typeParams.includes(o.value))
      );
      didRestore = true;
      payload.types = getToSelectData(WORD_TYPE)
        .filter((o) => typeParams.includes(o.value))
        .map((sl) => sl.value);
    }

    if (!typeParams.length) {
      setSelectedType(getToSelectData(WORD_TYPE));
      didRestore = true;
      payload.types = getToSelectData(WORD_TYPE).map((sl) => sl.value);
    }

    if (checkIfIncludeSentence.current) {
      const allow = searchParams.get("allowWithoutSentence");
      checkIfIncludeSentence.current.checked = allow !== "false";
      didRestore = true;
      payload.checkIfIncludeSentence = checkIfIncludeSentence.current.checked =
        allow !== "false";
    }

    if (!didRestore) return;

    const filters = Object.entries(payload).map(([key, value]) => ({
      id: key,
      value,
    }));
    filtersChangeHandler(filters);
  };

  const isSomeMutationPending =
    findLanguagesMutation.isLoading ||
    markOrderWordWithDecisionMutation.isLoading ||
    isLoading;

  useEffect(() => {
    findLanguagesMutation.mutate();
  }, []);

  return {
    isSomeMutationPending,
    addWord,
    setAddWord,
    options,
    selectedLang,
    setSelectedLang,
    setSelectedType,
    selectedType,
    selectedLevel,
    setSelectedLevel,
    handleSearch,
    checkIfIncludeSentence,
    limit,
    setLimit,
    skip,
    setSkip,
    itemsTotal,
    isLoading,
    filtersChangeHandler,
    sortingChangeHandler,
    tableData,
    HEADERS,
  };
};

export default useAdminWordListPage;
